import { PROJECTILE_CONFIG } from '../config.js';

export class Projectile {
  constructor(x, y, targetX, targetY, damage, color, owner, canvas, target = null) {
    this.x = x;
    this.y = y;
    this.startX = x;
    this.startY = y;
    this.speed = PROJECTILE_CONFIG.BASE_SPEED + (owner.level - 1) * 0.5; // Faster bullets for vets
    this.damage = damage;
    this.color = color;
    this.owner = owner; // Track who fired this
    this.active = true;
    this.trail = [];
    this.lifetime = 0;
    this.canvas = canvas;
    this.maxRange = owner.weaponRange; // Bullet can't travel farther than weapon range
    
    // Homing properties
    this.target = target; // The actual target object (for homing)
    
    // Check if fired from a base for perfect homing
    const isFiredFromBase = owner.constructor.name === 'TeamBase';
    
    if (isFiredFromBase) {
      // Perfect homing for base projectiles - fast turning, controlled speed
      this.homingStrength = 0.225; // 50% more aggressive homing (was 0.15)
      this.maxHomingTime = Infinity; // Never expires
      this.isPerfectHoming = true;
      this.speed *= 0.8; // Slightly reduce speed for better control
      this.maxFlightTime = 120; // 2 seconds at 60fps
      this.exploding = false;
      this.explosionTimer = 0;
      this.explosionX = 0;
      this.explosionY = 0;
    } else {
      // Subtle homing for soldier projectiles
      this.homingStrength = 0.02 + (owner.level - 1) * 0.005; // Subtle homing, increases with skill
      this.maxHomingTime = 30; // Only home for first 30 frames
      this.isPerfectHoming = false;
    }

    // Calculate initial direction
    const dx = targetX - x;
    const dy = targetY - y;
    const distance = Math.sqrt(dx * dx + dy * dy);
    this.vx = (dx / distance) * this.speed;
    this.vy = (dy / distance) * this.speed;
  }
  
  // Trigger explosion (called on impact)
  explode() {
    if (!this.exploding) {
      this.exploding = true;
      this.explosionTimer = 0;
      this.explosionX = this.x;
      this.explosionY = this.y;
      // Stop the projectile
      this.vx = 0;
      this.vy = 0;
    }
  }

  update() {
    this.lifetime++;

    // Store previous position for collision detection
    this.prevX = this.x;
    this.prevY = this.y;

    // Add to trail
    this.trail.push({x: this.x, y: this.y});
    if (this.trail.length > PROJECTILE_CONFIG.TRAIL_LENGTH) {
      this.trail.shift();
    }

    // Handle explosion state
    if (this.exploding) {
      this.explosionTimer++;
      // Different explosion durations for missiles vs regular projectiles
      const explosionDuration = this.isPerfectHoming ? PROJECTILE_CONFIG.MISSILE_EXPLOSION_DURATION : 8;
      if (this.explosionTimer > explosionDuration) {
        this.active = false;
      }
      // Keep position at explosion point
      this.x = this.explosionX;
      this.y = this.explosionY;
      return; // Don't update position or homing
    }
    
    // Apply subtle homing if target exists and still early in flight
    if (this.target && this.target.alive && this.lifetime < this.maxHomingTime) {
      const toTargetX = this.target.x - this.x;
      const toTargetY = this.target.y - this.y;
      const distanceToTarget = Math.sqrt(toTargetX * toTargetX + toTargetY * toTargetY);
      
      if (distanceToTarget > 0) {
        // Normalize direction to target
        const dirX = toTargetX / distanceToTarget;
        const dirY = toTargetY / distanceToTarget;
        
        // Gently adjust velocity toward target
        this.vx += dirX * this.homingStrength;
        this.vy += dirY * this.homingStrength;
        
        // Maintain speed (normalize velocity back to original speed)
        const currentSpeed = Math.sqrt(this.vx * this.vx + this.vy * this.vy);
        if (currentSpeed > 0) {
          this.vx = (this.vx / currentSpeed) * this.speed;
          this.vy = (this.vy / currentSpeed) * this.speed;
        }
      }
    }

    this.x += this.vx;
    this.y += this.vy;

    // Calculate distance traveled from start position
    const distanceTraveled = Math.sqrt(
      (this.x - this.startX) * (this.x - this.startX) + 
      (this.y - this.startY) * (this.y - this.startY)
    );

    // Remove if beyond weapon range, off screen, or too old
    // Perfect homing projectiles ignore range and lifetime limits
    if (!this.isPerfectHoming && distanceTraveled > this.maxRange ||
        this.x < -50 || this.x > this.canvas.width + 50 ||
        this.y < -50 || this.y > this.canvas.height + 50 ||
        (!this.isPerfectHoming && this.lifetime > PROJECTILE_CONFIG.MAX_LIFETIME)) {
      this.active = false;
    }
    
    // Perfect homing projectiles expire if target is destroyed OR max flight time reached
    if (this.isPerfectHoming) {
      if (this.target && (!this.target.alive || this.target.health <= 0)) {
        this.active = false;
      } else if (this.lifetime >= this.maxFlightTime) {
        // Start explosion instead of deactivating immediately
        if (!this.exploding) {
          this.exploding = true;
          this.explosionTimer = 0;
        } else {
          this.explosionTimer++;
          if (this.explosionTimer > 15) { // Explosion lasts 15 frames
            this.active = false;
          }
        }
      }
    }
  }

  render(ctx) {
    // Draw trail first
    for (let i = 0; i < this.trail.length; i++) {
      const point = this.trail[i];
      const alpha = (i / this.trail.length) * 0.5;
      ctx.globalAlpha = alpha;
      ctx.fillStyle = this.color;
      const size = (i / this.trail.length) * 4;
      ctx.fillRect(point.x - size/2, point.y - size/2, size, size);
    }
    ctx.globalAlpha = 1;

    // Enhanced visuals for perfect homing projectiles
    if (this.isPerfectHoming) {
      if (this.exploding) {
        // Explosion animation
        const explosionProgress = this.explosionTimer / 15; // 15 frames total
        const explosionRadius = explosionProgress * 20; // Expand to 20 pixels
        const explosionAlpha = 1 - explosionProgress; // Fade out
        
        // Multiple explosion rings
        for (let ring = 0; ring < 3; ring++) {
          const ringRadius = explosionRadius * (1 - ring * 0.3);
          const ringAlpha = explosionAlpha * (1 - ring * 0.2);
          
          ctx.globalAlpha = ringAlpha;
          ctx.strokeStyle = this.color;
          ctx.lineWidth = 3 - ring;
          ctx.beginPath();
          ctx.arc(this.x, this.y, ringRadius, 0, Math.PI * 2);
          ctx.stroke();
        }
        
        // Central flash
        ctx.globalAlpha = explosionAlpha;
        ctx.shadowBlur = 20;
        ctx.shadowColor = this.color;
        ctx.fillStyle = '#fff';
        const flashSize = explosionProgress * 8;
        ctx.fillRect(this.x - flashSize/2, this.y - flashSize/2, flashSize, flashSize);
        
        ctx.globalAlpha = 1;
        ctx.shadowBlur = 0;
      } else {
        // Normal perfect homing projectile
        // Intense pulsing glow for perfect homing
        const pulseIntensity = 0.8 + Math.sin(this.lifetime * 0.3) * 0.2;
        ctx.shadowBlur = PROJECTILE_CONFIG.GLOW_BLUR * 2 * pulseIntensity;
        ctx.shadowColor = this.color;
        
        // Larger, brighter projectile
        ctx.fillStyle = '#fff';
        ctx.fillRect(Math.floor(this.x) - 1, Math.floor(this.y) - 1, 3, 3);
        
        // Additional outer glow
        ctx.shadowBlur = PROJECTILE_CONFIG.GLOW_BLUR * 3;
        ctx.shadowColor = this.color;
        ctx.fillRect(Math.floor(this.x), Math.floor(this.y), 1, 1);
      }
    } else {
      // Normal projectile glow
      ctx.shadowBlur = PROJECTILE_CONFIG.GLOW_BLUR;
      ctx.shadowColor = this.color;
      
      // Bright white single pixel
      ctx.fillStyle = '#fff';
      ctx.fillRect(Math.floor(this.x), Math.floor(this.y), 1, 1);
    }

    ctx.shadowBlur = 0;
  }
}